import { useState, useCallback, useMemo, type ReactNode } from "react";
import { useFeedConfig } from "../../contexts/feedConfigContext";
import { usePlacement } from "../../attribution/placementContext";
import { createBetslipClick } from "../../attribution/sourceToken";
import { useBettingProvider } from "../../providers/bettingContext";
import { useI18n } from "../../i18n";
import { brandingButtonText } from "../../branding/localizedCopy";
import { useOddsStore } from "../../stores/oddsStore";
import {
  getAuthoredActionStakes,
  getEntries,
  getExecutionRecipe,
  getOutcomeIdsFromAction,
  getProviderCartItemsFromAction,
  getSelections,
  getTargetURLFromAction,
  executionRecipeShape,
  normalizeCouponType,
  selectionEntryIsExpired,
} from "../../utils/selectionSet";
import { openNavigationTarget } from "../../utils/navigationTargets";
import type { CopyEntryContext, FeedCTA, SelectionEntry, SelectionSet } from "../../types/feedContract";
import { CopyCountProof } from "./PostEngagement";


interface CardActionsProps {
  selectionSet?: SelectionSet | null;
  cta?: FeedCTA | null;
  postId: string;
  copyCount?: number;
  buttonContent?: ReactNode;
  buttonAriaLabel?: string;
  onCopyIntent?: () => Promise<void>;
  /**
   * Mixpanel's copy event, sent for the click itself. Separate from
   * `onCopyIntent` because that one is Wager Studios' copy counter, which only
   * counts a tail the provider actually took; the legacy widget's event counted
   * the click. See `postEngagementState`.
   */
  onCopyClick?: () => void;
  onCopy?: (postId: string, selectionIds: string[], entry?: CopyEntryContext) => void;
}

function actionableEntriesAt(
  entries: SelectionEntry[],
  providerType: string,
  now: number,
): SelectionEntry[] {
  return entries.filter((entry) => {
    if (selectionEntryIsExpired(entry, now)) return false;
    const action = entry.action;
    if (!action || typeof action !== "object") return false;
    if (action.type === "provider_cart") return true;
    if (action.type !== "betslip" || providerType === "none") return false;
    const outcomeIds = getOutcomeIdsFromAction(action);
    return outcomeIds.length > 0 && normalizeCouponType(action, outcomeIds.length) !== null;
  });
}

export function CardActions({
  selectionSet,
  cta,
  postId,
  copyCount,
  buttonContent,
  buttonAriaLabel,
  onCopyIntent,
  onCopyClick,
  onCopy,
}: CardActionsProps) {
  const { branding } = useFeedConfig();
  const i18n = useI18n();
  const { t } = i18n;
  const { addToBetslip, addToCart, type: providerType } = useBettingProvider();
  const placement = usePlacement();
  const oddsData = useOddsStore((s) => s.getOddsForActivity(postId));
  const [isAdded, setIsAdded] = useState(false);
  const [freshnessCheckedAt, setFreshnessCheckedAt] = useState(() => Date.now());
  const entries = useMemo(() => getEntries(selectionSet), [selectionSet]);
  const allSelectionIds = useMemo(
    () => getSelections(selectionSet).map((selection) => selection.id),
    [selectionSet],
  );
  const actionableEntries = useMemo(
    () => actionableEntriesAt(entries, providerType, freshnessCheckedAt),
    [entries, freshnessCheckedAt, providerType],
  );
  const expiredActionEntries = useMemo(
    () => entries.filter((entry) => Boolean(entry.action) && selectionEntryIsExpired(entry, freshnessCheckedAt)),
    [entries, freshnessCheckedAt],
  );

  const isSuspended = oddsData?.suspendedStatus === "full";
  const isClosed = oddsData?.closedStatus === "full";
  const isDisabled = isSuspended || isClosed;
  const buttonText = cta?.label || brandingButtonText(branding, i18n);

  const handleActionClick = useCallback(async () => {
    if (cta?.kind === "none" || isDisabled || isAdded) return;
    // Before the provider is asked, so a refused tail still reports.
    onCopyClick?.();
    // One click id per click: every entry it adds shares the token, and each
    // recorded copy carries the id so a placed bet's token joins back to it.
    const click = createBetslipClick("feed", placement);
    const clickSource = click ? { source: click.source } : {};
    const dispatchAt = Date.now();
    const currentActionableEntries = actionableEntriesAt(entries, providerType, dispatchAt);
    setFreshnessCheckedAt(dispatchAt);

    let acceptedAny = false;
    let fallbackURL: string | undefined;

    for (const entry of currentActionableEntries) {
      const action = entry.action;
      if (!action) continue;

      const selectionIds = entry.selection_ids.filter((id) => typeof id === "string" && id.length > 0);
      if (selectionIds.length === 0) continue;

      let providerOutcomeIds = Array.isArray(action.outcome_ids)
        ? action.outcome_ids.map(String).filter((id) => id.length > 0)
        : [];
      let accepted = false;
      let executedRecipe = false;

      if (action.type === "provider_cart") {
        const items = getProviderCartItemsFromAction(action);
        fallbackURL ||= getTargetURLFromAction(action) || cta?.target;
        const couponType = normalizeCouponType(action, items.length);
        if (items.length > 0 && providerType === "custom" && couponType) {
          accepted = addToCart(items, {
            updateMode: "append",
            ...clickSource,
            couponType,
          });
        }
      } else if (action.type === "betslip" && providerType !== "none") {
        const allOutcomeIds = getOutcomeIdsFromAction(action);
        // Legs the book has withdrawn cannot be tailed, but the remaining
        // legs still can: drop dead outcomes and downgrade a combination to
        // a single when only one leg survives. Authored stakes and the
        // execution recipe describe the full ticket, so a reduced ticket
        // carries neither.
        const legStates = oddsData?.legs;
        const outcomeIds = legStates
          ? allOutcomeIds.filter((id) => !legStates[String(id)]?.isClosed)
          : allOutcomeIds;
        const reduced = outcomeIds.length !== allOutcomeIds.length;
        const fullCouponType = normalizeCouponType(action, allOutcomeIds.length);
        const couponType = reduced && fullCouponType === "combination" && outcomeIds.length === 1
          ? "single"
          : fullCouponType;
        const executionRecipe = reduced ? null : getExecutionRecipe(action);
        executedRecipe = Boolean(executionRecipe);
        if (outcomeIds.length > 0 && couponType) {
          accepted = addToBetslip(outcomeIds, {
            updateMode: "append",
            ...clickSource,
            couponType,
            stakes: reduced
              ? undefined
              : getAuthoredActionStakes(entry, action, couponType, outcomeIds.length),
            ...(executionRecipe ? { executionRecipe } : {}),
          });
          if (accepted && reduced) {
            providerOutcomeIds = outcomeIds.map(String);
          }
        }
      }

      if (!accepted) continue;
      acceptedAny = true;
      const recipeShape = executedRecipe ? executionRecipeShape(getExecutionRecipe(action)) : null;
      const copyContext: CopyEntryContext = {
        entryId: entry.id,
        entryType: entry.kind,
        selectionIds,
        providerOutcomeIds,
        couponType: entry.coupon_type || action.coupon_type || "",
        ...(recipeShape ? { recipeShape } : {}),
        ...(click ? { clickId: click.clickId } : {}),
      };
      onCopy?.(postId, selectionIds, copyContext);
    }

    if (!acceptedAny) {
      if (fallbackURL) {
        onCopy?.(postId, allSelectionIds);
        openNavigationTarget(fallbackURL);
        if (cta?.kind === "provider_cart") await onCopyIntent?.();
      }
      return;
    }

    setIsAdded(true);
    try {
      await onCopyIntent?.();
    } finally {
      setTimeout(() => setIsAdded(false), 2000);
    }
  }, [
    entries,
    addToBetslip,
    addToCart,
    allSelectionIds,
    cta,
    isAdded,
    isDisabled,
    oddsData,
    onCopy,
    onCopyClick,
    onCopyIntent,
    placement,
    postId,
    providerType,
  ]);

  const handleNavigationClick = useCallback(async () => {
    if (cta?.kind === "none" || isDisabled || !cta?.target) return;
    if (cta.kind !== "provider_cart" && cta.kind !== "url" && cta.kind !== "deep_link" && cta.kind !== "link") return;
    // A content link has no bet to copy. Betting links still count the click
    // even when no provider integration is available on this surface.
    if (cta.kind === "provider_cart" || allSelectionIds.length > 0) onCopyClick?.();
    onCopy?.(postId, allSelectionIds);
    openNavigationTarget(cta.target);
    if (cta.kind === "provider_cart") await onCopyIntent?.();
  }, [allSelectionIds, cta, isDisabled, onCopy, onCopyClick, onCopyIntent, postId]);

  if (cta?.kind === "none") return null;
  const hasNavigationFallback = actionableEntries.length === 0
    && expiredActionEntries.length === 0
    && Boolean(cta?.target)
    && (cta?.kind === "provider_cart" || cta?.kind === "url" || cta?.kind === "deep_link" || cta?.kind === "link");
  if (actionableEntries.length === 0 && expiredActionEntries.length === 0 && !hasNavigationFallback) return null;

  const showWarning =
    oddsData?.suspendedStatus === "partial" ||
    oddsData?.closedStatus === "partial";

  return (
    <div className="card-actions">
      {showWarning && (
        <div className="card-actions-warning">
          {t("bet.someUnavailable")}
        </div>
      )}
      {expiredActionEntries.length > 0 && (
        <div className="card-actions-warning card-actions-warning-expired">
          {actionableEntries.length > 0
            ? t("bet.someExpired")
            : t("bet.priceExpiredRefresh")}
        </div>
      )}
      <CopyCountProof count={copyCount} label={branding.copyCountLabel} />
      <div className="card-actions-cta">
        {actionableEntries.length > 0 && (
          <button
            type="button"
            className={`card-actions-btn ${isAdded ? "card-actions-btn-added" : ""} ${isDisabled ? "card-actions-btn-disabled" : ""}`}
            onClick={() => void handleActionClick()}
            disabled={isDisabled}
            aria-label={buttonAriaLabel}
          >
            {isSuspended
              ? t("bet.suspended")
              : isClosed
                ? t("bet.closed")
                : isAdded
                  ? t("bet.added")
                  : buttonContent || buttonText}
          </button>
        )}
        {actionableEntries.length === 0 && expiredActionEntries.length > 0 && (
          <button
            type="button"
            className="card-actions-btn card-actions-btn-disabled"
            disabled
          >
            {t("bet.expired")}
          </button>
        )}
        {hasNavigationFallback && (
          <button
            type="button"
            className={`card-actions-btn ${isDisabled ? "card-actions-btn-disabled" : ""}`}
            onClick={() => void handleNavigationClick()}
            disabled={isDisabled}
            aria-label={buttonAriaLabel}
          >
            {isSuspended ? t("bet.suspended") : isClosed ? t("bet.closed") : buttonContent || buttonText}
          </button>
        )}
      </div>
    </div>
  );
}
